import React, { useRef, useState } from "react";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import { Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import PhonePicker from "@services/ui-components/src/PhonePicker/PhonePicker";
import { ReactSelect, Spinner, SpinnerLoader } from "@services/ui-components";
import { useReactiveVar } from "@apollo/client";
import DropPhotosMultiply from "../../../DropPhotosMultiply/DropPhotosMultiply";
import {
    useCreateNewCompanyMutation,
    useGetCountriesForCompanyQuery,
    useGetCountryGeoForCompanyQuery,
    useGetCurrenciesQuery,
    useGetGmtQuery,
    useGetLanguagesQuery,
} from "@me-team/host/main/company/graphql/company.hooks";
import LanguagesService from "../../../../services/LanguagesService/LanguagesService";
import { currentUserEmailVar } from "@me-team/host/main/globalVar/state";
import { refetchUserDataMenu } from "@me-team/host/src/apollo/apollo-client";
import { handlePaste } from "@me-team/host/src/utils/utils";
import { ValidationErrorExtensions } from "@me-team/host/src/utils/type";
import { useScrollToError } from "@me-team/host/src/utils/useScrollToError";
import { OptionString } from "../../../Warehouse/WarehousesOperations/types";
import { BusinessTypeSelector } from "../../../index";
import { BUSINESS_TYPE } from "../../../BusinessTypeSelector/businessType";
import { BusinessTypeSelectorControlProps } from "../../../CreateBranch/types";
import { serviceBusinessFormValidationSchema } from "../CreateOtherBusinessCompanyForm/constants";
import { useCountryCitiesPaginatedOptions } from "../../../../hooks/useCountryCitiesPaginatedOptions";
import CompanyCitySelectFields from "../../components/CompanyCitySelectFields";

interface InitialFormData {
    name: string;
    description: string;
    country: string;
    city: number;
    address: string;
    timezone: string;
    messageLanguage: string;
    phone: string;
    currency: string;
    email: string;
    website: string;
    workSchedule: string;
    image: string;
    customCity: string;
}

const CreateServiceBusinessCompanyForm: React.FC<BusinessTypeSelectorControlProps> = ({
    selectedBusinessType,
    businessTypeOptions,
    onBusinessTypeChange,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [countryId, setCountryId] = useState<number | null>(null);
    const nameFieldRef = useRef<HTMLInputElement>(null);
    const cityRef = useRef<HTMLDivElement>(null);
    const [photos, setPhotos] = useState([null]);
    const { data: timezoneData } = useGetGmtQuery();
    const { data: languagesData, loading: loadingLanguagesData } = useGetLanguagesQuery({
        variables: { isMessageOnly: true },
    });
    const { data: currenciesData, loading: currenciesDataLoading } = useGetCurrenciesQuery();
    const [createCompany] = useCreateNewCompanyMutation();
    const currentUserEmail = useReactiveVar(currentUserEmailVar);
    const [showCustomCityInput, setShowCustomCityInput] = useState<boolean>(false);
    const { data: countriesData } = useGetCountriesForCompanyQuery();
    const { data: countryGeoData, loading: loadingCountryGeo } = useGetCountryGeoForCompanyQuery({
        onCompleted(data) {
            const defaultCountry = data?.countryGeo?.country;
            if (defaultCountry?.id) {
                setCountryId(defaultCountry.id);
            }
        },
    });

    const {
        cityOptions,
        selectedCityOption,
        isCitiesInitialLoading,
        isCitiesSearchLoading,
        onCityMenuOpen,
        onCityMenuClose,
        onCitySearchChange,
        onCityMenuScrollToBottom,
        resetCities,
    } = useCountryCitiesPaginatedOptions({
        countryId,
        selectedCity: null,
        includeCustomCityOption: true,
        customCityOptionLabel: t("Other city"),
    });

    const countryOptions =
        countriesData?.relations.countries.map((country) => ({
            label: country.name,
            value: country.id.toString(),
        })) || [];

    const timezoneOptions =
        timezoneData?.relations.timezones.map((timezone) => ({
            label: timezone.name,
            value: timezone.id.toString(),
        })) || [];

    const currenciesOptions =
        currenciesData?.relations.currencies.map((currency) => ({
            label: `${currency.code} - ${currency.currency}`,
            value: currency.id.toString(),
        })) || [];

    const countryGeo = countryGeoData?.countryGeo?.country;

    const initialValues: InitialFormData = {
        name: "",
        description: "",
        country: countryGeo?.id.toString() || "",
        city: null,
        address: "",
        timezone: countryGeo?.timezone.length ? countryGeo?.timezone[0].id.toString() : "",
        messageLanguage: "",
        phone: countryGeo?.phoneCode?.code ? `+${countryGeo?.phoneCode?.code}` : "",
        currency: countryGeo?.currencies.length ? countryGeo?.currencies[0].id.toString() : "",
        email: currentUserEmail?.trim() || "",
        website: "",
        workSchedule: "",
        image: null,
        customCity: null,
    };

    const handleSubmit = async (
        values: InitialFormData,
        { setSubmitting, setFieldError }: FormikHelpers<InitialFormData>
    ) => {
        const selectedCityId = values.city ? Number(values.city) : null;

        const input = {
            category: BUSINESS_TYPE.SERVICE,
            name: values.name,
            country: Number(values.country),
            city: showCustomCityInput ? null : selectedCityId,
            otherCity: values?.customCity ? values?.customCity : null,
            address: values.address,
            phone: values.phone,
            email: values.email?.trim() || "",
            website: values.website,
            timezone: +values.timezone,
            currency: +values.currency,
            workSchedule: values.workSchedule,
            description: values.description,
            messageLanguage: +values.messageLanguage,
            images: photos,
            logo: values.image,
            deleted: false,
        };

        const validPhotos = photos.filter((photo) => photo != null);
        input.images = validPhotos;

        try {
            await createCompany({
                variables: { input },
                onCompleted: async (data) => {
                    const branchId: string = String(data.createCompany.branches[0].id);
                    await refetchUserDataMenu();
                    branchId && navigate(`/create-owner/${branchId}`, { replace: true });
                },
            });
            setSubmitting(false);
        } catch (error) {
            if (error?.graphQLErrors?.[0]?.extensions?.category === "company.already_exists") {
                setFieldError("name", t("Company with the received name and phone number already exists!"));

                if (nameFieldRef.current) {
                    nameFieldRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                    nameFieldRef.current.focus();
                }
                return;
            }
            const validationErrors = (error.graphQLErrors[0].extensions as unknown as ValidationErrorExtensions)
                .validation;
            const websiteErrors = validationErrors.website;
            if (Array.isArray(websiteErrors)) {
                const longError = websiteErrors.find((e) =>
                    e.message.includes("This value is too long. It should have 2040 characters or less.")
                );
                if (longError) {
                    setFieldError("website", t("The value length for this field must not exceed 2040 characters."));
                }
            }
            console.error("Error creating branch:", error);
            setSubmitting(false);
        }
    };

    const isBaseLoading = loadingCountryGeo || loadingLanguagesData || currenciesDataLoading;

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={serviceBusinessFormValidationSchema(t)}
            onSubmit={(values, actions) => handleSubmit(values, actions)}
            enableReinitialize
        >
            {({ setFieldValue, handleBlur, values, isSubmitting, errors, touched, submitCount }) => {
                const handleChangeCountry = async (selectedOption: OptionString) => {
                    const newCountryId = selectedOption?.value ? parseInt(selectedOption.value, 10) : null;

                    setFieldValue("city", null);
                    setFieldValue("customCity", null);
                    setShowCustomCityInput(false);
                    setFieldValue("country", selectedOption?.value || "");
                    setCountryId(newCountryId);

                    const selectedCountry = countriesData?.relations.countries.find(
                        (country) => country.id === newCountryId
                    );
                    if (selectedCountry?.timezone?.length) {
                        setFieldValue("timezone", selectedCountry.timezone[0].id.toString());
                    } else {
                        setFieldValue("timezone", "");
                    }

                    resetCities();
                };

                /* eslint-disable */
                useScrollToError({ submitClickedCount: submitCount, errors, refs: { city: cityRef } });
                return (
                    <Form>
                        <Row className="mt-6">
                            <Col lg={9} className="pe-lg-5">
                                <div className="mb-4">
                                    <BusinessTypeSelector
                                        title={t("Business type")}
                                        selectedBusinessType={selectedBusinessType}
                                        options={businessTypeOptions}
                                        onChange={onBusinessTypeChange}
                                        disabled={isBaseLoading}
                                    />
                                </div>
                                {isBaseLoading ? (
                                    <Spinner heightContent />
                                ) : (
                                    <>
                                        <h5 className="fs-4 text-capitalize mb-4">{t("Basic information")}</h5>
                                        <div>
                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="registration-name-field"
                                                            className="fs-7 fw-bold"
                                                        >
                                                            <span className="required-label">{t("Company name")}</span>
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} xxl={10}>
                                                        <div className="position-relative">
                                                            <Field
                                                                id="registration-name-field"
                                                                name="name"
                                                                innerRef={nameFieldRef}
                                                                maxLength={50}
                                                                placeholder={t("Enter a company name")}
                                                                className={`form-control rounded-1 lh-22 pe-5 text-truncate ${errors.name && touched.name ? "border-danger" : "border-secondary"}`}
                                                            />
                                                            <div className="pt-1 position-absolute top-50 translate-middle-y end-0 me-3">
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t(
                                                                                "This data will be displayed in the online booking form"
                                                                            )}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i className="bi bi-info-circle"></i>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Col sm={9} xxl={10} className="ms-auto">
                                                    <ErrorMessage name="name">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                </Col>
                                            </div>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-country-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("country")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <ReactSelect
                                                        id="registration-country-field"
                                                        name="country"
                                                        inputAutoComplete="new-password"
                                                        options={countryOptions}
                                                        value={
                                                            countryOptions.find(
                                                                (option) =>
                                                                    String(option.value) === String(values.country)
                                                            ) ?? null
                                                        }
                                                        onChange={handleChangeCountry}
                                                        placeholder={t("Select country")}
                                                        isSearchable
                                                    />
                                                    <ErrorMessage name="country">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <CompanyCitySelectFields
                                                cityFieldId="registration-city-field"
                                                customCityFieldId="registration-customCity-field"
                                                cityRef={cityRef}
                                                cityOptions={cityOptions}
                                                selectedCityOption={selectedCityOption}
                                                showCustomCityInput={showCustomCityInput}
                                                setShowCustomCityInput={setShowCustomCityInput}
                                                isLoading={isCitiesInitialLoading || isCitiesSearchLoading}
                                                disabled={values.country === ""}
                                                onMenuOpen={onCityMenuOpen}
                                                onMenuClose={onCityMenuClose}
                                                onInputChange={onCitySearchChange}
                                                onMenuScrollToBottom={onCityMenuScrollToBottom}
                                            />

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-address-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("address")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            id="registration-address-field"
                                                            name="address"
                                                            autoComplete="address-line1"
                                                            placeholder={t("Enter your location")}
                                                            maxLength={200}
                                                            className={`form-control rounded-1 lh-22 pe-5 ${errors.address && touched.address ? "border-danger" : "border-secondary"}`}
                                                        />
                                                        <div className="pt-1 position-absolute top-50 translate-middle-y end-0 me-3">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data will be displayed in the online booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle"></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="address">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-phone-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("phone")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <PhonePicker
                                                        id="registration-phone-field"
                                                        name="phone"
                                                        value={values.phone}
                                                    />
                                                    <ErrorMessage name="phone">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-email-field"
                                                        className="fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("email")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <Field
                                                        id="registration-email-field"
                                                        name="email"
                                                        placeholder={t("Add E-mail")}
                                                        className="form-control rounded-1 lh-22"
                                                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                            const trimmedValue = e.target.value.trim();
                                                            e.target.value = trimmedValue;
                                                            setFieldValue("email", trimmedValue, false);
                                                            handleBlur(e);
                                                        }}
                                                    />
                                                    <ErrorMessage name="email">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-website-field"
                                                        className="fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("website")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <Field
                                                        id="registration-website-field"
                                                        name="website"
                                                        placeholder={t("Site address")}
                                                        className={`form-control rounded-1 lh-22 pe-5 ${errors.website && touched.website ? "border-danger" : "border-secondary"}`}
                                                        maxLength={2040}
                                                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                                                            handlePaste(e, 2040)
                                                        }
                                                    />
                                                    <ErrorMessage name="website">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="registration-timezone-field"
                                                            className="required-label fs-7 fw-bold"
                                                        >
                                                            {t("timezone")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} xxl={10}>
                                                        <ReactSelect
                                                            id="registration-timezone-field"
                                                            name="timezone"
                                                            options={timezoneOptions}
                                                            value={timezoneOptions.find(
                                                                (option: any) => option.value === values.timezone
                                                            )}
                                                            onChange={(selectedOption: any) => {
                                                                setFieldValue(
                                                                    "timezone",
                                                                    selectedOption ? selectedOption.value : ""
                                                                );
                                                            }}
                                                            placeholder={t("Select Gmt")}
                                                            isSearchable
                                                        />
                                                    </Col>
                                                </Row>
                                                <Col sm={9} xxl={10} className="ms-auto">
                                                    <ErrorMessage name="timezone">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                </Col>
                                            </div>

                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="registration-messageLanguage-field"
                                                            className="required-label fs-7 fw-bold"
                                                        >
                                                            {t("Message language")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} xxl={10}>
                                                        <ReactSelect
                                                            id="registration-messageLanguage-field"
                                                            name="messageLanguage"
                                                            options={LanguagesService.languageFilter(
                                                                languagesData.relations.languages
                                                            )}
                                                            value={LanguagesService.languageFilter(
                                                                languagesData.relations.languages
                                                            ).find(
                                                                (option: any) => option.value === values.messageLanguage
                                                            )}
                                                            onChange={(selectedOption: any) => {
                                                                setFieldValue(
                                                                    "messageLanguage",
                                                                    selectedOption ? selectedOption.value : ""
                                                                );
                                                            }}
                                                            placeholder={t("Select Language")}
                                                            isSearchable
                                                        />
                                                    </Col>
                                                </Row>
                                                <Col sm={9} xxl={10} className="ms-auto">
                                                    <ErrorMessage name="messageLanguage">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                </Col>
                                            </div>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-currency-field"
                                                        className="required-label fs-7 fw-bold mt-sm-2 pt-1"
                                                    >
                                                        {t("Currency")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <ReactSelect
                                                        id="registration-currency-field"
                                                        name="currency"
                                                        options={currenciesOptions}
                                                        value={currenciesOptions.find(
                                                            (option: any) => option.value === values.currency
                                                        )}
                                                        onChange={(selectedOption: any) => {
                                                            setFieldValue(
                                                                "currency",
                                                                selectedOption ? selectedOption.value : ""
                                                            );
                                                        }}
                                                        placeholder={t("Select Currency")}
                                                        isSearchable
                                                    />
                                                    <ErrorMessage name="currency">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="registration-workSchedule-field"
                                                            className="required-label fs-7 fw-bold"
                                                        >
                                                            {t("Work schedule")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} xxl={10}>
                                                        <div className="position-relative">
                                                            <Field
                                                                id="registration-workSchedule-field"
                                                                name="workSchedule"
                                                                placeholder={t("Work schedule")}
                                                                maxLength={50}
                                                                className={`form-control rounded-1 lh-22 pe-5 ${errors.workSchedule && touched.workSchedule ? "border-danger" : "border-secondary"}`}
                                                            />
                                                            <div className="pt-1 position-absolute top-50 translate-middle-y end-0 me-3">
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t(
                                                                                "This data will be displayed in the online booking form"
                                                                            )}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i className="bi bi-info-circle"></i>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Col sm={9} xxl={10} className="ms-auto">
                                                    <ErrorMessage name="workSchedule">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                    <div className="mt-1 px-2">
                                                        <small className="text-grey">
                                                            {t("for example, Mon-Sun : 11:00-22:00")}
                                                        </small>
                                                    </div>
                                                </Col>
                                            </div>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="registration-description-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("description")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            as="textarea"
                                                            id="registration-description-field"
                                                            name="description"
                                                            placeholder={t("description")}
                                                            maxLength={200}
                                                            className="form-control pe-5 rounded-1"
                                                        />
                                                        <div className="pt-1 position-absolute top-0 end-0 mt-1 me-3">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data will be displayed in the online booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle"></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="description">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                    <div className="fs-7 text-grey text-start mt-1 px-1">
                                                        {`${values.description.length}/200`}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4 pt-6">
                                                <Col sm={3} xxl={2} className="mb-2 mb-sm-0">
                                                    <label htmlFor="registration-photo-field" className="fs-7 fw-bold">
                                                        {t("Photo of the branch")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} xxl={10}>
                                                    <div className="mb-2">
                                                        <p className="fs-7 mb-0">
                                                            {t(
                                                                "Add jpeg images of the branch premises, interior, etc."
                                                            )}
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data will be displayed in the online booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle ms-1 fs-7 pt-1"></i>
                                                            </OverlayTrigger>
                                                        </p>
                                                    </div>
                                                    <Row className="gx-0">
                                                        <DropPhotosMultiply
                                                            photosArray={photos}
                                                            setPhotos={setPhotos}
                                                        />
                                                    </Row>
                                                    <div>
                                                        <p className="fs-7 text-grey mb-6">{t("max-size")}</p>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </>
                                )}
                            </Col>
                            <Col lg={3} className="ms-auto ps-lg-0">
                                <h5 className="fs-4 mb-4">{t("logo")}</h5>
                                <ImageCrop setFieldValue={setFieldValue} modalTitle={t("Company logo")} />
                            </Col>
                        </Row>
                        <div className="my-4 mt-lg-0">
                            <button
                                type="submit"
                                className="btn btn-primary fw-bold custom-btn rounded-1"
                                disabled={isSubmitting || isBaseLoading}
                            >
                                {isSubmitting ? (
                                    <SpinnerLoader variant="light" loadingText={t("Creating...")} />
                                ) : (
                                    t("Create")
                                )}
                            </button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default React.memo(CreateServiceBusinessCompanyForm);
